// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {AfterViewInit, Component, DestroyRef, inject, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {D3eService} from '../../../services/d3e-service';
import {HeaderService} from '../../header.service';
import {UserService} from '../../../services/user-service';
import {NotificationService} from '../../../services/notification-service';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {
    IBatchUpdateDialogResult,
    IBulkUpdatePbaSerialRequest,
    ILicensedDrive,
    IUpdatePbaSerialRequest
} from '../../../types/licensedDrives';
import {
  AddSsdSerialDialogComponent,
} from './add-ssd-serial-dialog/add-ssd-serial-dialog.component';
import {UploadOrEnterCodeDialogComponent} from './upload-or-enter-code-dialog/upload-or-enter-code-dialog.component';
import {SelectedOrgMethod} from '../../../shared/selectedOrg';
import { map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import {MatSort, SortDirection} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {AlertDialogComponent} from '../../dialogs/alert/alert.component';
import {LicenseDetailsComponent} from './license-details/license-details.component';
import {EVENT_TYPES} from '../../../types/events';
import {EventsService} from '../../../services/events-service';
import {IColumnChooserElement} from '../../shared/components/column-chooser/column-chooser.component';
import {MatRipple} from '@angular/material/core';
import {ClipboardService} from '../../../services/clipboard.service';
import {GenerateConfirmationDialogComponent} from './generate-confirmation-dialog/generate-confirmation-dialog.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BatchUpdateCustomDialogComponent} from './batch-update-custom-dialog/batch-update-custom-dialog.component';
import {isMSP} from '../../../shared/permissions-helper';
import { withLoading } from '../../../shared/misc';
import {MatChipInputEvent, MatChipEditedEvent} from '@angular/material/chips';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

@UntilDestroy({checkProperties: true})
@Component({
    selector: 'app-assign',
    templateUrl: './assign-licenses.component.html',
    styleUrls: ['./assign-licenses.component.scss'],
    standalone: false
})
export class AssignLicensesComponent implements OnInit, AfterViewInit {
  private destroyRef = inject(DestroyRef);
  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChildren(MatRipple) ripples: QueryList<MatRipple>;

  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  showAssigned: boolean = null;
  lastSelectedIndex = -1;
  pbaLicensesCount: number = 0;

  custom1Filters: string[] = [];
  custom2Filters: string[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  successRippleColor = 'rgba(33,150,243,0.27)';
  errorRippleColor = 'rgba(244,67,54,0.87)';
  rippleColor = this.successRippleColor;

  private selectedOrgId: string;
  private hasUserBeenSet: boolean;
  licensedDrives = new MatTableDataSource<ILicensedDrive>([]);

  columns: IColumnChooserElement[] = [];
  sortAttrs = [{'attr': 'disk_serial', 'desc': false}];
  columnsToDisplay = ['disk_serial', 'created_dttm', 'last_generated', 'pba_license_id', 'license_key'];
  mspColumnsToDisplay = ['checkbox', 'disk_serial', 'created_dttm', 'last_generated', 'pba_license_id', 'license_key'];

  licenses_available = '';
  subscription_id = null;

  sortingOnGenerated = false;
  selectAllChecked = false;
  isMsp: boolean = false;
  addSerialDialog: MatDialogRef<AddSsdSerialDialogComponent, any> = null;
  pendingDrivesSelected = false;
  checkedDrives: ILicensedDrive[] = [];
  max_licenses: number = 0;
  licenses_used: number = 0;
  totalNewLicensesRequired: number = 0;

  sortAttr = 'disk_serial';
  sortDir: SortDirection = 'desc';
  raid = false;
  enable_pba_extended_features: boolean = false;
  enable_pba_log_erase: boolean = false;
  ssdSerialNumberSearch = '';
  customText1Search = '';
  customText2Search = '';

  constructor(
    public dialog: MatDialog,
    public d3eService: D3eService,
    private headerService: HeaderService,
    private userService: UserService,
    private notification: NotificationService,
    public route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private eventsService: EventsService,
    private readonly clipboard: ClipboardService,
  ) {
    this.headerService.setTitle(translate.instant('assign.header'));
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-file-certificate-outline');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(true);

    route.params.subscribe(params => {
      this.subscription_id = params.subscription_id;
    });

    this.userService.getUser()
      .pipe(untilDestroyed(this))
      .subscribe(user => {
        this.columns = [
          {
            title: this.translate.instant('assign.columns.ssd-serial-number'),
            value: 'disk_serial',
            selected: true,
            hidden: false,
          },
          {
            title: this.translate.instant('assign.columns.custom-1'),
            value: 'custom_text_1',
            selected: false,
            hidden: false,
          },
          {
            title: this.translate.instant('assign.columns.custom-2'),
            value: 'custom_text_2',
            selected: false,
            hidden: false,
          },
          {
            title: this.translate.instant('assign.columns.creatd'),
            value: 'created_dttm',
            selected: true,
            hidden: false,
          },
          {
            title: this.translate.instant('assign.columns.license-generated'),
            value: 'last_generated',
            selected: true,
            hidden: false,
          },
          {
            title: this.translate.instant('assign.columns.license-id'),
            value: 'pba_license_id',
            selected: true,
            hidden: false,
          },
          {
            title: this.translate.instant('assign.columns.maintenance-date'),
            value: 'pba_license_maint_end',
            selected: true,
            hidden: false,
          },
          {
            title: this.translate.instant('assign.columns.license-key'),
            value: 'license_key',
            selected: true,
            hidden: true,
          },
        ];
        this.isMsp = isMSP(user);
        if (this.isMsp) {
          const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
          if (selectedOrg == '') {
            this.router.navigateByUrl('products');
          }
          this.columnsToDisplay = this.mspColumnsToDisplay;
          this.columns.unshift({title: 'select', value: 'checkbox', selected: true, hidden: true,});
        }
      });

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if (this.hasUserBeenSet) {
          router.navigateByUrl('products');
        }

        this.hasUserBeenSet = true;
      });

  }

  ngOnInit(): void {
    this.getLicensedDrives(true);
    // These values are passed by passing state in the html in the product sub display components
    // data-defense-sub.component - bitlocker-sub.component - hardware-sub.component
    let state = window.history.state;
    this.raid = state?.raid;
    this.enable_pba_extended_features = state?.subscription?.enable_pba_extended_features;
    this.enable_pba_log_erase = state?.subscription?.enable_pba_log_erase;

    this.licensedDrives.data = [];
    this.licenses_available = this.translate.instant('assign.assigned_count', {
      licenses_used: 0,
      licenses_available: 0
    });
  }

  ngAfterViewInit(): void {
    if (this.ripples) {
      this.ripples.forEach(r => r.disabled = true);
    }
  }

  selectAll(mouseEvent: MouseEvent) {
    let checkedCount = this.licensedDrives.data.reduce((a, e) => e.checked ? a + 1 : a, 0);
    if (checkedCount === this.licensedDrives.data.length) {
      this.licensedDrives.data.forEach(d => {
        d.checked = false;
      });
    } else {
      this.licensedDrives.data.forEach(d => {
        d.checked = true;
      });
    }
    this.checkSelectAllLater(null, mouseEvent);
  }

  checkSelects(drive: ILicensedDrive, mouseEvent: MouseEvent) {
    const thisDriveIndex = this.licensedDrives.data.indexOf(drive);

    if (mouseEvent?.shiftKey && drive && this.lastSelectedIndex > -1) {
      const fromIndex = Math.min(this.lastSelectedIndex, thisDriveIndex);
      const toIndex = Math.max(this.lastSelectedIndex, thisDriveIndex);
      for (let i = fromIndex; i <= toIndex; ++i) {
        this.licensedDrives.data[i].checked = drive.checked;
      }
    }

    this.checkedDrives = this.licensedDrives.data.filter(d => d.checked);
    this.pendingDrivesSelected = this.checkedDrives.length > 0;
    this.totalNewLicensesRequired = this.checkedDrives.filter(d => !d.pba_license_id).length;
    this.selectAllChecked = this.checkedDrives.length > 0 && this.checkedDrives.length === this.licensedDrives.data.length;

    if (mouseEvent != null) {
      this.lastSelectedIndex = thisDriveIndex;
    }
  }

  checkSelectAllLater(drive: ILicensedDrive, mouseEvent: MouseEvent) {
    setTimeout(() => {
      this.checkSelects(drive, mouseEvent);
    }, 0);
  }

  buildQuery() {
    const search = {
      disk_serial: this.ssdSerialNumberSearch,
      custom_text_1: this.custom1Filters,
      custom_text_2: this.custom2Filters,
    };

    return {
      sorts: this.sortAttrs,
      search: search,
      offset: this.licensedDrives.data.length,
      assigned: this.showAssigned,
    };
  }

  getLicensedDrives(clearList: boolean = true, sortOnGenerated: boolean = false) {
    if (clearList) {
      this.licenses_available = '';
      this.licensedDrives.data = [];
      this.pbaLicensesCount = 0;
      this.max_licenses = 0;
      this.licenses_used = 0;
      this.totalNewLicensesRequired = 0;
      this.licenses_available = this.translate.instant('assign.assigned_count', {
        licenses_used: 0,
        licenses_available: 0
      });
    }
    if (sortOnGenerated) {
      this.sortAttr = 'last_generated';
      this.sortDir = 'desc';
      this.sortAttrs = [{'attr': 'last_generated', 'desc': true}];
      this.sortingOnGenerated = true;
    }
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    let query = this.buildQuery();
    if (this.subscription_id != null) {
      this.d3eService.getPbaSerials(selectedOrg, this.subscription_id, query)
        .pipe(
            withLoading(isLoading => this.isLoading$.next(isLoading))
        )
        .subscribe(response => {
          this.pbaLicensesCount = response.pba_licenses_count;
          this.max_licenses = response.subscription_count;
          this.licenses_used = response.licenses_used;
          let max = this.max_licenses.toString(10);
          if (this.max_licenses > 1000000) {
            max = this.translate.instant('Unlimited');
          }
          let newList: ILicensedDrive[] = [];
          this.licenses_available = this.translate.instant('assign.assigned_count',
            {licenses_used: this.licenses_used, licenses_available: max});
          if (response.licenses) {
            response.licenses.forEach(drive => {
              if (drive.pba_license_id) {
                const pba_license = response.pba_licenses[drive.pba_license_id.toString()];
                if (pba_license) {
                  drive.pba_license = pba_license;
                }
              }
              drive.checked = this.selectAllChecked;
              newList.push(drive);
            });
          }
          this.licensedDrives.data = [...this.licensedDrives.data, ...newList];
          this.checkSelectAllLater(null, null);
        },
        error => {
          console.error(`Error retrieving assigned drives: ${error}`);
          this.checkSelectAllLater(null, null);
        });
    }
  }

  sortColumn(event) {
    const sort = {
      'attr': event.active,
      'desc': (event.direction === 'desc')
    };
    if (event.active === 'last_generated' || event.active === 'pba_license_id') {
      this.sortingOnGenerated = true;
      this.sortAttrs = [sort];
      // add secondary sort on serial
      const secondary_sort = {
        'attr': 'disk_serial',
        'desc': (event.direction === 'desc')
      };
      this.sortAttrs.push(secondary_sort);
    } else {
      this.sortingOnGenerated = false;
      this.sortAttrs = [sort];
      if (event.active === 'pba_license_maint_end') {
        sort['table_name'] = 'pba_license';
        sort['attr'] = 'maint_exp_timestamp';
      }
    }
    this.getLicensedDrives(true);
  }

  showAddSerialDialog() {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    const data = {
      selectedOrgId: selectedOrg,
      subscriptionId: this.subscription_id,
    };
    this.addSerialDialog = this.dialog.open(AddSsdSerialDialogComponent, {
      minWidth: '40em',
      data: data,
    });

    this.addSerialDialog.componentInstance.serialSubmitted.subscribe(() => {
      this.sortAttrs = [{'attr': 'created_dttm', 'desc': true}];
      this.sortAttr = 'created_dttm';
      this.sortDir = 'desc';
      this.sortingOnGenerated = false;
      this.getLicensedDrives(true);
    });
  }

  showDeleteSerialDialog() {
    if (this.checkedDrives.length != 1) {
      return;
    }
    let driveToDelete = this.checkedDrives[0];
    if (driveToDelete.pba_license_id) {
      this.notification.showError(this.translate.instant('assign.pba.delete-license.error'));
      return;
    }

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '500px',
      data: {
        title: this.translate.instant('assign.delete.title'),
        content: this.translate.instant('assign.delete.confirm-text', {disk_serial: driveToDelete.disk_serial}),
        rejectBtnName: this.translate.instant['NO'],
        acceptBtnName: this.translate.instant['YES'],
        showRejectBtn: true,
      },
    });

    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result) {
          const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
          this.d3eService.deletePbaLicense(selectedOrg, this.subscription_id, driveToDelete.disk_serial)
            .subscribe({
              next: () => {
                this.notification.showSuccess(this.translate.instant('assign.delete.success',
                  {'disk_serial': driveToDelete.disk_serial}));
                this.getLicensedDrives(true);
              },
              error: () => {
                this.notification.showError(this.translate.instant('assign.delete.error',
                  {'disk_serial': driveToDelete.disk_serial}));
                this.getLicensedDrives(true);
              }
            });
        }
      },
      error: () => {
        this.notification.showError(this.translate.instant('assign.delete.error',
          {'disk_serial': driveToDelete.disk_serial}));
      }
    });
  }

  showEnterOrUploadCodeDialog() {
    const dialogRef = this.dialog.open(UploadOrEnterCodeDialogComponent);
    dialogRef.afterClosed().pipe(
      mergeMap(result => {
        let request_code;
        if (result) {
          request_code = result;
          const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
          this.d3eService.uploadRequestCode(selectedOrg, this.subscription_id, request_code).subscribe({
            next: () => {
              this.getLicensedDrives(true, true);
              this.notification.showSuccess(this.translate.instant('assign.upload-or-enter-code-dialog.success'));
            },
            error: error => {
              const errorText = 'assign.pba.upload-generate-failed.' + ((error as any)?.['reason'] ?? 'text');
              this.notification.showError(
                this.translate.instant(errorText),
                this.translate.instant('assign.pba.upload-generate-failed.title')
              );
              this.getLicensedDrives(true);
            }
          });
        }
        return EMPTY;
      }),
    ).subscribe();
  }

  showBatchUpdateCustomDialog() {
    const dialogClosed$: Observable<IBatchUpdateDialogResult> = this.dialog.open(BatchUpdateCustomDialogComponent).afterClosed();
    dialogClosed$.pipe(
      withLatestFrom(this.headerService.orgSelectedChanged),
      switchMap(([result, selectedOrgId]: [IBatchUpdateDialogResult, string]) => {
        const update: IBulkUpdatePbaSerialRequest = {
          pba_serial_ids: this.licensedDrives.data.filter(d => d.checked).map(d => d.id)
        };
        if (result.update1) {
          update.custom_text_1 = result.custom1 || '';
        }
        if (result.update2) {
          update.custom_text_2 = result.custom2|| '';
        }
        return this.d3eService.bulkUpdatePbaSerial(selectedOrgId == 'All' ? '' : selectedOrgId, update)
      }),
    ).subscribe({
      next: () => this.getLicensedDrives(),
      error: error => console.error(error)
    });
  }

  onCopyResponseCode(license_key: string, pba_license_id: number) {
    this.clipboard.copyMessage(license_key, this.translate.instant('assign.pba.copy-message')).then((success) => {
      if (success) {
        const type = EVENT_TYPES.PBA_LICENSE_COPIED;
        const description = 'A PBA License was copied';
        const detail = 'subscription: ' + this.subscription_id + ', pba_license_id: ' + pba_license_id;
        this.eventsService.info({type, description, detail}).subscribe();
      }
    });
  }

  create_download_file(drive: ILicensedDrive) {
    const type = EVENT_TYPES.PBA_FILE_DOWNLOAD;
    const description = 'A PBA License was downloaded';
    const detail = 'subscription: ' + this.subscription_id + ', pba license id: ' + drive.pba_license.id;
    this.eventsService.info({type, description, detail}).subscribe();
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(drive.pba_license.license_key));
    const licType = this.raid ? "Enterprise" : "Client";
    const licDate = dayjs.utc(drive.pba_license.created_dttm).format('YYYYMMDD');
    const filename = `PBA_${licDate}_${licType}_${drive.pba_license.id}.lic`;
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  showGenerateDialog() {
    let count = this.checkedDrives.length;
    if (!count) {
      this.pendingDrivesSelected = false;
      return;
    }

    if ((this.totalNewLicensesRequired + this.licenses_used) > this.max_licenses) {
      this.notification.showError(this.translate.instant('assign.generate.not-enough-licenses',
        {
          'requested': this.totalNewLicensesRequired + this.licenses_used,
          'maximum': this.max_licenses
        })
      );
      return;
    }

    let how_many = count.toString();
    if (this.selectAllChecked) {
      how_many = this.translate.instant('ALL');
    }

    this.headerService.orgSelectedChanged.pipe(
      switchMap(selectedOrgId => this.d3eService.get_subscriptions(selectedOrgId)),
      map(subscriptions => {
        return subscriptions.filter(subscription => subscription.subscription_id == this.subscription_id)[0];
      }),
      switchMap(subscription => this.dialog.open(GenerateConfirmationDialogComponent, {
          width: '500px',
          data: {
            how_many,
            maintEnd: new Date(subscription.maint_exp_timestamp ?? subscription.exp_timestamp)
          },
        }).afterClosed()
      ),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(maintEnd => {
      if (maintEnd) {
        const selectedSerials = this.checkedDrives.map(d => d.disk_serial);
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
        this.d3eService.generatePbaLicense(selectedOrg, this.subscription_id, maintEnd, selectedSerials).subscribe(() => {
            this.notification.showSuccess(this.translate.instant('assign.generate.success'));
            this.getLicensedDrives(true, true);
          },
          error => {
            const errorText = 'assign.pba.upload-generate-failed.' + ((error as any)?.['reason'] ?? 'text');
            this.notification.showError(
              this.translate.instant(errorText),
              this.translate.instant('assign.pba.upload-generate-failed.title')
            );
            this.getLicensedDrives(true);
          });
      }
    });
  }

  showDetailsDialog(disk_serial) {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    this.d3eService.getPbaLicenseDetails(selectedOrg, this.subscription_id, disk_serial)
      .subscribe({
        next: result => {
          this.dialog.open(LicenseDetailsComponent, {
            width: '600px',
            data: result,
          });
        },
        error: () => {
          this.notification.showError(this.translate.instant('assign.details.failure'));
        }
      });
  }

  updateDisplayedColumns(columns: string[]) {
    this.columnsToDisplay = columns;
  }

  onCustomTextBlur(drive: ILicensedDrive, index: number, event: Event) {
    const target = event.target as HTMLInputElement;
    const inputName = target.name;
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    drive.custom_text_1 = drive.custom_text_1.trim();
    drive.custom_text_2 = drive.custom_text_2.trim();
    const request: IUpdatePbaSerialRequest = {
      'subscription_id': this.subscription_id,
      'disk_serial': drive.disk_serial,
      'custom_text_1': drive.custom_text_1,
      'custom_text_2': drive.custom_text_2,
    };

    index = inputName === 'CustomText1' ? index : index + (this.ripples.length / 2);
    this.d3eService.updatePbaSerial(selectedOrg, request).subscribe({
      error: () => {
        this.rippleColor = this.errorRippleColor;
        setTimeout(() => {
          const ripple: MatRipple = this.ripples.find((r, i) => i === index);
          ripple?.launch({
            centered: true,
            animation: {
              enterDuration: 500,
              exitDuration: 1000
            }
          });
          setTimeout(() => {
            if (this.ripples && this.ripples[index]) {
              this.ripples[index].disabled = true;
            }
          }, 1500);
        }, 0);
      },
      complete: () => {
        this.rippleColor = this.successRippleColor;
        const ripple: MatRipple = this.ripples.find((r, i) => i === index);
        setTimeout(() => {
          ripple?.launch({
            centered: true,
            animation: {
              enterDuration: 500,
              exitDuration: 1000
            }
          });
          setTimeout(() => {
            if (this.ripples && this.ripples[index]) {
              this.ripples[index].disabled = true;
            }
          }, 1500);
        }, 0);
      }
    });
  }

  onScrollHandler(ev) {
    if (!this.isLoading$.getValue()) {
      this.getLicensedDrives(false, false);
    }
  }

  setButtonToggle(event) {
    this.showAssigned = event.value;
    this.getLicensedDrives(true, false);
  }

  get totalShown() {
    return this.licensedDrives.data.length;
  }

  get totalSelected() {
    return this.licensedDrives.data.filter(d => d.checked).length;
  }

  addCustomFilter(filters: string[], event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      filters.push(value);
      this.getLicensedDrives(true);
    }
    event.chipInput!.clear();
  }

  removeCustomFilter(filters: string[], filterString: string): void {
    const index = filters.indexOf(filterString);
    if (index >= 0) {
      filters.splice(index, 1);
      this.getLicensedDrives(true);
    }
  }

  editCustomFilter(filters: string[], filterString: string, event: MatChipEditedEvent) {
    const value = event.value.trim();
    const index = filters.indexOf(filterString);
    if (index >= 0) {
      filters[index] = value;
      this.getLicensedDrives(true);
    }
  }

}
